// ---------------------------------------------------------------------------
// Shared domain types for the Word Add-in
// ---------------------------------------------------------------------------

/** Verdict returned by the fact-checker validation API. */
export type Verdict = 'supported' | 'unsupported' | 'contradicted';

/** Status of a validation card before/after the API call resolves. */
export type ClaimValidationStatus = 'pending' | 'validating' | 'done' | 'error';

/** One piece of evidence (supporting or contradicting). */
export interface EvidenceChunk {
  text: string;
  source: string;
  url?: string | null;
  source_type: string;
  relevance_score?: number | null;
}

/** A single atomic claim extracted from the document text. */
export interface ExtractedClaim {
  /** UUID from the API (may be absent for inline-only validation). */
  claim_id: string;
  /** The atomic proposition text. */
  text: string;
  claim_type: string | null;
  confidence: number | null;
  /** 0-based position within the parent sentence. */
  position: number;
}

/** The full card state shown in the UI. */
export interface ClaimCard {
  /** Stable key: claim_id from extraction, or `pos-{i}` fallback. */
  key: string;
  /** Stable claim_id for referencing the extraction API. */
  claim_id?: string;
  /** The claim text to display and validate. */
  text: string;
  /** Which sentence this came from (0-based). */
  sentence_position: number;
  validationStatus: ClaimValidationStatus;
  verdict: Verdict | null;
  confidence: number | null;
  reasoning: string | null;
  supporting_chunks: EvidenceChunk[];
  contradicting_chunks: EvidenceChunk[];
  /** Which validation backend produced this result (e.g. paper_index, web_search). */
  source_types_used: string[];
  error?: string;
}

/** Summary counts shown in the SummaryBar. */
export interface ValidationSummary {
  total: number;
  supported: number;
  unsupported: number;
  contradicted: number;
}

// ---------------------------------------------------------------------------
// API response shapes (mirrors fact-checker Pydantic models)
// ---------------------------------------------------------------------------

export interface ApiResponse<T> {
  data: T | null;
  error: { code: string; message: string } | null;
  request_id: string;
}

export interface ClaimOut {
  claim_id: string;
  text: string;
  claim_type: string | null;
  confidence: number | null;
  position: number;
}

export interface SentenceOut {
  sentence_id: string;
  text: string;
  position: number;
  claims: ClaimOut[];
}

export interface ExtractionResults {
  sentences: SentenceOut[];
  total_sentences: number;
  total_claims: number;
}

export interface ExtractJobOut {
  job_id: string;
  status: string;
  created_at: string;
  updated_at: string | null;
  results: ExtractionResults | null;
  error: string | null;
}

export interface ValidationResultOut {
  position: number;
  claim_id: string | null;
  claim_text: string;
  verdict: Verdict;
  confidence: number | null;
  reasoning: string;
  supporting_chunks: EvidenceChunk[];
  contradicting_chunks: EvidenceChunk[];
  source_types_used: string[];
}

export interface ValidateJobOut {
  job_id: string;
  status: string;
  created_at: string;
  updated_at: string | null;
  results: ValidationResultOut[] | null;
  summary: ValidationSummary | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Checklist service types (mirrors checklist service Pydantic models)
// ---------------------------------------------------------------------------

export type ReviewVerdict = 'pass' | 'fail' | 'na';
export type SectionMatchType = 'exact' | 'regex' | 'document' | null;

export interface ChecklistOut {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string | null;
  created_by: string | null;
}

export interface RuleOut {
  id: string;
  title: string;
  description: string | null;
  condition: string | null;
  criteria: string;
  section_match: string | null;
  section_match_type: SectionMatchType;
  created_at: string;
  updated_at: string | null;
  created_by: string | null;
}

export interface ChecklistDetailOut extends ChecklistOut {
  rules: RuleOut[];
}

export interface ChecklistList {
  checklists: ChecklistOut[];
  total: number;
  limit: number;
  offset: number;
}

export interface RuleList {
  rules: RuleOut[];
  total: number;
  limit: number;
  offset: number;
}

export interface ReviewResultOut {
  rule_id: string;
  rule_title: string;
  rule_condition: string | null;
  rule_criteria: string;
  section_match: string | null;
  section_match_type: SectionMatchType;
  condition_met: boolean | null;
  verdict: ReviewVerdict;
  reasoning: string | null;
  evidence: string[];
}

export interface ReviewSummary {
  total: number;
  pass_count: number;
  fail_count: number;
  na_count: number;
}

export interface EvaluateJobCreated {
  job_id: string;
  status: string;
  total_rules: number;
  created_at: string;
}

export interface EvaluateJobOut {
  job_id: string;
  status: string;
  project_id: string | null;
  checklist_id: string | null;
  results: ReviewResultOut[] | null;
  summary: ReviewSummary | null;
  error: string | null;
  created_at: string;
  updated_at: string | null;
}

// ---------------------------------------------------------------------------
// Content generation service types (mirrors content-gen service Pydantic models)
// ---------------------------------------------------------------------------

export type PromptSectionMatchType = 'exact' | 'regex' | 'any' | null;

export type GenerationApproach = 'llm' | 'aries' | 'paperqa';

export const GENERATION_APPROACH_LABELS: Record<GenerationApproach, string> = {
  llm: 'LLM',
  aries: 'Aries',
  paperqa: 'PaperQA (literature)',
};

export interface PaperIndexOut {
  id: string;
  name: string;
  description: string | null;
  site_hostname: string | null;
  site_path: string | null;
  index_key: string | null;
  updated_at: string | null;
}

export interface UserSettingsOut {
  default_paper_index_id: string | null;
}

export interface PromptVariableDef {
  name: string;
  description: string | null;
  default: string | null;
  required: boolean;
}

export interface PromptLibraryOut {
  id: string;
  name: string;
  description: string | null;
  prompt_count: number;
  created_at: string;
  updated_at: string;
}

export interface PromptOut {
  id: string;
  library_id: string;
  title: string;
  description: string | null;
  template: string;
  section_match: string | null;
  section_match_type: PromptSectionMatchType;
  variables: PromptVariableDef[];
  system_prompt: string | null;
  model: string | null;
  max_tokens: number | null;
  position: number;
  approach: GenerationApproach;
  created_at: string;
  updated_at: string;
}

export interface PromptLibraryList {
  items: PromptLibraryOut[];
  total: number;
}

export interface PromptList {
  items: PromptOut[];
  total: number;
}

export interface GenerateJobCreated {
  job_id: string;
  status: string;
  created_at: string;
}

export interface GenerateSentence {
  text: string;
  citation_indices: number[];
}

export interface GenerateContent {
  section_title: string | null;
  sentences: GenerateSentence[];
  notes: string | null;
  references: unknown[];
  metadata: Record<string, unknown>;
}

export interface GenerationProgress {
  messages: string[];
}

export interface GenerateJobOut {
  job_id: string;
  status: string;
  result: GenerateContent | null;
  progress: GenerationProgress | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Critic service types (mirrors critic service Pydantic models)
// ---------------------------------------------------------------------------

export interface PersonaOut {
  id: string;
  name: string;
  description: string | null;
  system_prompt: string;
  focus_areas: string[];
  model: string | null;
  max_top_items: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface PersonaList {
  personas: PersonaOut[];
  total: number;
  limit: number;
  offset: number;
}

export interface CritiqueItem {
  title: string;
  detail: string;
}

export interface CritiqueSectionIn {
  title: string;
  content: string;
}

export interface CritiqueContent {
  document?: string;
  sections?: CritiqueSectionIn[];
}

export interface Recommendation {
  priority: 'high' | 'medium' | 'low';
  title: string;
  detail: string;
}

export interface CritiqueResult {
  summary: string;
  score: number;
  strengths: CritiqueItem[];
  weaknesses: CritiqueItem[];
  recommendations: Recommendation[];
}

export interface CritiqueJobCreated {
  job_id: string;
  status: string;
  persona_id: string;
  created_at: string;
}

export interface CritiqueJobOut {
  job_id: string;
  status: string;
  persona_id: string;
  persona_name: string | null;
  result: CritiqueResult | null;
  error: string | null;
  created_at: string;
  updated_at: string | null;
}
